import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'

import { Separator } from '@/components/ui/separator'
import {
  BillingFormData,
  NewClientData,
} from '@/containers/billing/register/billingPortals/newRegisterWizard/NewRegisterWizard'
import { cn } from '@/lib/utils'

import { ClientSelector, NewClientForm } from './components'
import { ClientData, ClientValidationErrors } from './types'

// Simplified validation schema
const clientManagerSchema = z.object({
  mode: z.enum(['select', 'create']),
  selectedClientId: z.string().optional(),
  clientNotes: z.string().optional(),
})

// Conditional validation based on mode and required prop
const createValidationSchema = (required: boolean) => {
  if (!required) return clientManagerSchema

  return clientManagerSchema.refine(
    (data) => {
      if (data.mode === 'select') {
        return !!data.selectedClientId
      }
      return true // For create mode, we validate newClientData separately
    },
    {
      message: 'Debe seleccionar un cliente',
      path: ['selectedClientId'],
    }
  )
}

type FormData = z.infer<typeof clientManagerSchema>

// Data structure that matches BillingFormData expectations
export interface ClientManagerResult {
  clientId?: string
  isNewClient: boolean
  newClientData?: NewClientData
  selectedClientData?: ClientData
  clientNotes?: string
}

// Exposed methods through ref
export interface ClientManagerRef {
  submit: () => Promise<void>
}

interface ClientManagerProps {
  // Event callbacks (SOLID: Dependency Inversion)
  onSubmit?: (_data?: ClientManagerResult) => void | Promise<void>
  onValidationChange?: (_isValid: boolean) => void
  onDataChange?: (_data?: ClientManagerResult) => void // New callback for data sync

  // Configuration
  required?: boolean
  disabled?: boolean
  className?: string
  clientContainer?: boolean

  // Initial values
  data: Partial<BillingFormData>
}

export const ClientManager = forwardRef<ClientManagerRef, ClientManagerProps>(
  (
    {
      required = true,
      disabled = false,
      clientContainer = false,
      className,
      data,
      onSubmit,
      onValidationChange,
      onDataChange,
    },
    ref
  ) => {
    // Internal state for new client data and selected client data
    const [newClientData, setNewClientData] = useState<
      NewClientData | undefined
    >(data?.newClientData)
    const [selectedClientData, setSelectedClientData] = useState<
      ClientData | undefined
    >(data?.selectedClientData as ClientData | undefined)
    const [validationErrors, setValidationErrors] = useState<
      Record<string, string>
    >({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

    // Main form with react-hook-form
    const form = useForm<FormData>({
      resolver: zodResolver(createValidationSchema(required)),
      mode: 'onChange',
      defaultValues: {
        mode: data?.isNewClient ? 'create' : 'select',
        selectedClientId: data?.clientId,
      },
    })

    const { control, watch, setValue, handleSubmit } = form
    const currentMode = watch('mode')
    const watchedClientNotes = watch('clientNotes')
    const watchedSelectedClientId = watch('selectedClientId')

    // Validate all client data (both existing and new clients)
    const validateClientData = useCallback((): {
      isValid: boolean
      errors: Record<string, string>
    } => {
      const errors: Record<string, string> = {}

      if (currentMode === 'create') {
        // For new client creation, need complete newClientData
        if (!newClientData) {
          errors.general = 'Datos del nuevo cliente no válidos'
          return { isValid: false, errors }
        }

        if (!newClientData.name?.trim()) {
          errors.name = 'El nombre es obligatorio'
        } else if (newClientData.name.length < 2) {
          errors.name = 'El nombre debe tener al menos 2 caracteres'
        } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(newClientData.name)) {
          errors.name = 'El nombre solo puede contener letras y espacios'
        }

        if (!newClientData.lastName?.trim()) {
          errors.lastName = 'Los apellidos son obligatorios'
        } else if (newClientData.lastName.length < 2) {
          errors.lastName = 'Los apellidos deben tener al menos 2 caracteres'
        } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(newClientData.lastName)) {
          errors.lastName =
            'Los apellidos solo pueden contener letras y espacios'
        }

        // if (!newClientData.email?.trim()) {
        //   errors.email = 'El correo electrónico es obligatorio'
        // } else

        if (
          newClientData.email &&
          !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
            newClientData.email
          )
        ) {
          errors.email = 'El correo electrónico no tiene un formato válido'
        }

        if (newClientData.phone?.number?.trim()) {
          if (!/^\d{8,10}$/.test(newClientData.phone.number)) {
            errors.phone =
              'El teléfono debe contener solo números y tener entre 8 y 10 dígitos'
          }
        } else {
          delete errors.phone
        }

        if (!newClientData.phone?.codeCountry?.trim()) {
          errors.phoneCode = 'El código de país es obligatorio'
        }

        if (!newClientData.mainAddress?.trim())
          errors.mainAddress = 'La calle principal es obligatoria'
        if (!newClientData.country?.name?.trim())
          errors.country = 'El país es obligatorio'
        if (!newClientData.province?.id?.trim())
          errors.province = 'La provincia es obligatoria'
        if (!newClientData.municipality?.id?.trim())
          errors.municipality = 'El municipio es obligatorio'
      } else {
        // For existing client selection, need clientId and selectedClientData
        if (required && (!watchedSelectedClientId || !selectedClientData)) {
          errors.general = 'Debe seleccionar un cliente para continuar'
        }
      }

      const isValidResult = Object.keys(errors).length === 0
      return { isValid: isValidResult, errors }
    }, [
      currentMode,
      newClientData,
      watchedSelectedClientId,
      selectedClientData,
      required,
    ])

    // Helper to notify parent of validation status
    const notifyValidationChange = useCallback(
      (isValid: boolean) => {
        onValidationChange?.(isValid)
      },
      [onValidationChange]
    )

    // Helper to notify parent of state changes (for data sync only)
    const notifyDataChange = useCallback(() => {
      const currentData: ClientManagerResult = {
        clientId: watchedSelectedClientId,
        isNewClient: currentMode === 'create',
        selectedClientData:
          currentMode === 'select' ? selectedClientData : undefined,
        newClientData: currentMode === 'create' ? newClientData : undefined,
        clientNotes: watchedClientNotes || '',
      }

      onDataChange?.(currentData)
    }, [
      currentMode,
      watchedSelectedClientId,
      watchedClientNotes,
      selectedClientData,
      newClientData,
      onDataChange,
    ])

    // Handle form submission (for internal submit buttons and external triggers)
    const onFormSubmit = useCallback(
      async (formData: FormData) => {
        setHasAttemptedSubmit(true)

        // Validate all client data
        const validation = validateClientData()

        if (!validation.isValid) {
          setValidationErrors(validation.errors)
          notifyValidationChange(false)
          return
        }

        setIsSubmitting(true)
        const submitData: ClientManagerResult = {
          clientId: formData.selectedClientId,
          isNewClient: currentMode === 'create',
          selectedClientData:
            currentMode === 'select' ? selectedClientData : undefined,
          newClientData: currentMode === 'create' ? newClientData : undefined,
          clientNotes: formData.clientNotes,
        }

        // Clear errors on successful validation
        setValidationErrors({})
        notifyValidationChange(true)
        await onSubmit?.(submitData)
        setIsSubmitting(false)
      },
      [
        currentMode,
        selectedClientData,
        newClientData,
        validateClientData,
        onSubmit,
        notifyValidationChange,
      ]
    )

    // Handle switch to new client mode
    const handleNewClientClick = useCallback(
      (initialName = '') => {
        setValue('mode', 'create')
        setValue('selectedClientId', undefined)
        // DON'T clear selectedClientData - preserve it for when user switches back
        // setSelectedClientData(undefined)
        setNewClientData({
          name: initialName,
          lastName: '',
          email: '',
          phone: { number: '', codeCountry: '+53' },
          categories: [],
          mainAddress: '',
          country: {
            id: '54',
            name: 'Cuba',
          },
          province: { id: '', name: '' },
          municipality: { id: '', name: '' },
        })
        setValidationErrors({})
        setHasAttemptedSubmit(false)

        // When switching to new client mode, data is not valid yet but allow user to fill form
        notifyValidationChange(true) // Allow user to interact with form
      },
      [notifyValidationChange, setValue]
    )

    // Expose submit function through ref
    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          const formData = {
            mode: currentMode,
            selectedClientId: watchedSelectedClientId,
            clientNotes: watchedClientNotes,
          }
          await onFormSubmit(formData)
        },
      }),
      [currentMode, watchedSelectedClientId, watchedClientNotes, onFormSubmit]
    )

    // Only run validation when user has attempted to submit or we have existing errors
    useEffect(() => {
      if (hasAttemptedSubmit) {
        const validation = validateClientData()
        setValidationErrors(validation.errors)
        notifyValidationChange(validation.isValid)
      }
    }, [
      currentMode,
      watchedSelectedClientId,
      newClientData,
      selectedClientData,
      hasAttemptedSubmit,
      validateClientData,
      notifyValidationChange,
    ])

    useEffect(() => {
      if (clientContainer) handleNewClientClick()
    }, [clientContainer, handleNewClientClick])

    // Notify parent of data changes (for synchronization only, not submission)
    useEffect(() => {
      notifyDataChange()
    }, [notifyDataChange])

    // Initial validation state - assume valid until user tries to submit
    useEffect(() => {
      if (!hasAttemptedSubmit) {
        // Start with valid state to allow user interaction
        notifyValidationChange(true)
      }
    }, [notifyValidationChange, hasAttemptedSubmit])

    // Handle client selection
    const handleClientSelect = (client: ClientData | null) => {
      if (!client) return

      setValue('mode', 'select')
      setValue('selectedClientId', client.id.toString())
      // Convert ClientData to ClientInterface format
      const clientInterface: ClientData = {
        id: client.id,
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        name:
          client.name ||
          `${client.firstName || ''} ${client.lastName || ''}`.trim(),
        email: client.email || '',
        ci: client.ci ? client?.ci : undefined,
        observations: '', // Always null for ClientInterface
        commercial: client.commercial
          ? {
              id: client.commercial.id,
              name: client.commercial.name, // Map name to username
            }
          : undefined,
        phones: client.phones || [],
      }
      setSelectedClientData(clientInterface)
      setNewClientData(undefined)

      // Clear errors and reset submit state when user makes a selection
      setValidationErrors({})
      setHasAttemptedSubmit(false)

      // Since user made a valid selection, notify parent that data is now valid
      notifyValidationChange(true)
    }

    // Handle switch back to search
    const handleSwitchToSearch = () => {
      setValue('mode', 'select')

      // If we have preserved selectedClientData, restore it
      if (selectedClientData) {
        setValue('selectedClientId', selectedClientData.id.toString())
      } else {
        setValue('selectedClientId', undefined)
      }

      // DON'T clear selectedClientData - it's already preserved
      // setSelectedClientData(undefined)
      setNewClientData(undefined)
      setValidationErrors({})
      setHasAttemptedSubmit(false)

      // When switching back to search, validate based on whether we have a selection
      notifyValidationChange(!!selectedClientData || !required)
    }

    // Handle new client data changes
    const handleNewClientDataChange = (updates: Partial<NewClientData>) => {
      if (newClientData) {
        const updatedData = { ...newClientData, ...updates }
        setNewClientData(updatedData)

        // Clear errors for changed fields only if user hasn't attempted submit
        if (!hasAttemptedSubmit) {
          const clearedErrors = { ...validationErrors }
          Object.keys(updates).forEach((key) => {
            clearedErrors[key] = ''
          })
          setValidationErrors(clearedErrors)
        }
      }
    }

    // Handle field change to clear internal errors only
    const handleFieldChange = (field: keyof ClientValidationErrors) => {
      if (!hasAttemptedSubmit) {
        const clearedErrors = { ...validationErrors }
        clearedErrors[field] = ''
        setValidationErrors(clearedErrors)
      }
    }

    // Handle focus on fields to clear validation errors
    const handleFieldFocus = (field: string) => {
      // Clear internal error only if user hasn't attempted submit
      if (!hasAttemptedSubmit) {
        const clearedErrors = { ...validationErrors }
        clearedErrors[field] = ''
        setValidationErrors(clearedErrors)
      }
    }

    return (
      <form
        className={cn('space-y-6', className)}
        onSubmit={handleSubmit(onFormSubmit)}
      >
        {/* Client Selection or New Client Form */}

        {currentMode === 'select' ? (
          <Controller
            control={control}
            name="selectedClientId"
            render={({ fieldState }) => (
              <ClientSelector
                disabled={disabled || isSubmitting}
                error={fieldState.error?.message || validationErrors.general}
                onClientSelect={handleClientSelect}
                onFocus={() => handleFieldFocus('general')}
                onNewClientClick={handleNewClientClick}
                required={required}
                selectedClient={selectedClientData}
              />
            )}
          />
        ) : (
          <>
            <Separator />
            {newClientData && (
              <NewClientForm
                clientContainer={clientContainer}
                clientData={newClientData}
                disabled={disabled || isSubmitting}
                onClientDataChange={handleNewClientDataChange}
                onFieldChange={handleFieldChange}
                onFieldFocus={handleFieldFocus}
                onSwitchToSearch={handleSwitchToSearch}
                validationErrors={validationErrors}
              />
            )}
          </>
        )}
      </form>
    )
  }
)

ClientManager.displayName = 'ClientManager'
